import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash, ArrowRight, ArrowLeft, FileText, Spinner, X, DownloadSimple, Printer, WarningCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const DIAMETERS = [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const PAYMENT_METHODS = ['Espèces', 'Carte bancaire', 'Virement', 'Chèque'];
const TVA = 0.20;
const roundEuro = (v) => Math.round(v);
const round2 = (v) => Math.round(v * 100) / 100;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const INITIAL = {
  invoice_type: 'crevaison',
  client_name: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'France',
  billing_email: '',
  vehicle_plate: '',
  vehicle_model: '',
  // intervention address (differs from billing)
  intervention_address_differs: false,
  intervention_address: '',
  // intervention date (differs from invoice date)
  intervention_date_differs: false,
  intervention_date: '',
  // crevaison
  repair_price: '',
  // montage — full tire size
  tire_width: '',
  tire_height: '',
  tire_diameter: '',
  tire_brand: '',
  quantity: '1',
  price_tire: '',
  price_montage: '',
  has_travel: false,
  travel_price: '',
  // libre
  custom_lines: [{ description: '', quantity: '1', unit_price: '' }],
  // payment
  payment_method: 'Carte bancaire',
  payment_date: todayISO(),
  // invoice number override
  override_number: '',
};

export default function ManualInvoiceDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1=type+common, 2=type-specific+payment, 3=preview
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatedNumber, setGeneratedNumber] = useState(null);
  const [nextNaturalNumber, setNextNaturalNumber] = useState(null);

  // Fetch the next natural invoice number when opening the dialog
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/api/invoices/next-number`, { headers: getAuthHeaders() })
      .then((r) => setNextNaturalNumber(r.data.next_number))
      .catch(() => setNextNaturalNumber(null));
  }, [open]);

  const reset = () => {
    setStep(1);
    setData(INITIAL);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setGeneratedNumber(null);
    setSubmitting(false);
  };

  const closeAll = () => {
    reset();
    onOpenChange(false);
  };

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const updateLine = (i, k, v) =>
    setData((p) => ({
      ...p,
      custom_lines: p.custom_lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)),
    }));
  const addLine = () =>
    setData((p) => ({
      ...p,
      custom_lines: [...p.custom_lines, { description: '', quantity: '1', unit_price: '' }],
    }));
  const removeLine = (i) =>
    setData((p) => ({
      ...p,
      custom_lines: p.custom_lines.filter((_, idx) => idx !== i),
    }));

  // ============ Live computed lines & totals ============
  const computed = useMemo(() => {
    const makeLine = (description, ttc, qty) => {
      const amount = roundEuro(ttc);
      const q = Math.max(parseInt(qty || 1, 10), 1);
      const pu_ht = round2(amount / q / (1 + TVA));
      return { description, pu_ht, qty: q, tva_rate: 20, total_ttc: amount };
    };
    const lines = [];
    if (data.invoice_type === 'crevaison') {
      const p = parseFloat(data.repair_price);
      if (p > 0) lines.push(makeLine('Réparation crevaison', p, 1));
    } else if (data.invoice_type === 'montage') {
      const dia = parseInt(data.tire_diameter, 10);
      const width = parseInt(data.tire_width, 10);
      const height = parseInt(data.tire_height, 10);
      const brand = (data.tire_brand || '').trim();
      const qty = Math.max(parseInt(data.quantity || 1, 10), 1);
      const pt = parseFloat(data.price_tire) || 0;
      const pm = parseFloat(data.price_montage) || 0;
      const tp = parseFloat(data.travel_price) || 0;
      const size = width > 0 && height > 0 ? `${width}/${height} R${dia}` : `R${dia}`;
      const tireDesc = `Pneu ${size}${brand ? ` — ${brand}` : ''}`;
      const montageDesc = `Montage pneu R${dia}`;
      if (dia > 0 && pt > 0) lines.push(makeLine(tireDesc, pt * qty, qty));
      if (dia > 0 && pm > 0) lines.push(makeLine(montageDesc, pm * qty, qty));
      if (data.has_travel && tp > 0)
        lines.push(makeLine('Forfait déplacement dépannage véhicule immobilisé', tp, 1));
    } else if (data.invoice_type === 'libre') {
      data.custom_lines.forEach((cl) => {
        const desc = (cl.description || '').trim();
        const q = Math.max(parseInt(cl.quantity || 1, 10), 1);
        const pu = parseFloat(cl.unit_price) || 0;
        if (desc && pu > 0) lines.push(makeLine(desc, pu * q, q));
      });
    }
    const total_ttc = round2(lines.reduce((s, l) => s + l.total_ttc, 0));
    const total_ht = round2(total_ttc / (1 + TVA));
    const tva_amount = round2(total_ttc - total_ht);
    return { lines, total_ttc, total_ht, tva_amount };
  }, [data]);

  // ============ Validation ============
  const step1Valid =
    data.client_name.trim() &&
    data.address_line1.trim() &&
    data.postal_code.trim() &&
    data.city.trim() &&
    data.vehicle_plate.trim() &&
    data.vehicle_model.trim();

  const step2Valid =
    computed.lines.length > 0 &&
    computed.total_ttc > 0 &&
    data.payment_method &&
    data.payment_date;

  // ============ Generate ============
  const generate = async () => {
    setSubmitting(true);
    try {
      const billingAddress = `${data.address_line1}${data.address_line2 ? ', ' + data.address_line2 : ''}, ${data.postal_code} ${data.city}`;
      const interventionAddr = data.intervention_address_differs && data.intervention_address.trim()
        ? data.intervention_address.trim()
        : billingAddress;
      const interventionDate = data.intervention_date_differs && data.intervention_date
        ? data.intervention_date
        : '';
      const payload = {
        invoice_type: data.invoice_type,
        client_name: data.client_name.trim(),
        billing_email: data.billing_email.trim(),
        address: interventionAddr,
        intervention_address: interventionAddr,
        intervention_date: interventionDate,
        address_line1: data.address_line1.trim(),
        address_line2: data.address_line2.trim(),
        postal_code: data.postal_code.trim(),
        city: data.city.trim(),
        country: data.country.trim() || 'France',
        vehicle_plate: data.vehicle_plate.trim(),
        vehicle_model: data.vehicle_model.trim(),
        payment_method: data.payment_method,
        payment_date: data.payment_date,
        override_number: (data.override_number || '').trim(),
      };
      if (data.invoice_type === 'crevaison') {
        payload.repair_price = parseFloat(data.repair_price);
      } else if (data.invoice_type === 'montage') {
        payload.tire_width = parseInt(data.tire_width, 10) || 0;
        payload.tire_height = parseInt(data.tire_height, 10) || 0;
        payload.tire_diameter = parseInt(data.tire_diameter, 10);
        payload.tire_brand = (data.tire_brand || '').trim();
        payload.quantity = parseInt(data.quantity, 10);
        payload.price_tire = parseFloat(data.price_tire) || 0;
        payload.price_montage = parseFloat(data.price_montage) || 0;
        payload.has_travel = !!data.has_travel;
        payload.travel_price = parseFloat(data.travel_price) || 0;
      } else if (data.invoice_type === 'libre') {
        payload.custom_lines = data.custom_lines
          .filter((l) => l.description.trim() && parseFloat(l.unit_price) > 0)
          .map((l) => ({
            description: l.description.trim(),
            quantity: parseInt(l.quantity, 10) || 1,
            unit_price: parseFloat(l.unit_price),
          }));
      }

      const res = await axios.post(`${API}/api/invoices/manual`, payload, {
        headers: getAuthHeaders(),
      });
      const number = res.data.invoice_number;
      setGeneratedNumber(number);

      // Fetch the PDF for preview
      const pdfRes = await axios.get(`${API}/api/invoices/${number}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setStep(3);
      toast.success(`Facture ${number} générée`);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!generatedNumber) return;
    try {
      const res = await axios.get(`${API}/api/invoices/${generatedNumber}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  // ============ Render ============
  return (
    <>
      <Dialog open={open && step < 3} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-reco-600" />
              Nouvelle facture manuelle
              <span className="text-xs text-gray-500 ml-2 font-normal">Étape {step} / 2</span>
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4 mt-2">
              {/* Type selection */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Type de facture</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { v: 'crevaison', label: 'Crevaison', desc: 'Réparation crevaison' },
                    { v: 'montage', label: 'Montage', desc: 'Pneus + déplacement' },
                    { v: 'libre', label: 'Ajout libre', desc: 'Lignes personnalisées' },
                  ].map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => update('invoice_type', t.v)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        data.invoice_type === t.v
                          ? 'border-reco-500 bg-reco-50 ring-2 ring-reco-500/20'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      data-testid={`manual-type-${t.v}`}
                    >
                      <div className="font-medium text-sm">{t.label}</div>
                      <div className="text-xs text-gray-500">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Common fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom du client *</Label>
                  <Input value={data.client_name} onChange={(e) => update('client_name', e.target.value)} data-testid="manual-client-name" />
                </div>
                <div>
                  <Label className="text-xs">Email (facultatif)</Label>
                  <Input type="email" value={data.billing_email} onChange={(e) => update('billing_email', e.target.value)} data-testid="manual-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Adresse ligne 1 *</Label>
                  <Input value={data.address_line1} onChange={(e) => update('address_line1', e.target.value)} data-testid="manual-addr1" placeholder="Ex: 12 rue de la Paix" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Adresse ligne 2 (facultatif)</Label>
                  <Input value={data.address_line2} onChange={(e) => update('address_line2', e.target.value)} placeholder="Complément d'adresse" />
                </div>
                <div>
                  <Label className="text-xs">Code postal *</Label>
                  <Input value={data.postal_code} onChange={(e) => update('postal_code', e.target.value)} data-testid="manual-postal" />
                </div>
                <div>
                  <Label className="text-xs">Ville *</Label>
                  <Input value={data.city} onChange={(e) => update('city', e.target.value)} data-testid="manual-city" />
                </div>
                <div>
                  <Label className="text-xs">Pays</Label>
                  <Input value={data.country} onChange={(e) => update('country', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Immatriculation *</Label>
                  <Input value={data.vehicle_plate} onChange={(e) => update('vehicle_plate', e.target.value.toUpperCase())} data-testid="manual-plate" placeholder="AB-123-CD" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Marque / Modèle du véhicule *</Label>
                  <Input value={data.vehicle_model} onChange={(e) => update('vehicle_model', e.target.value)} data-testid="manual-model" placeholder="Ex: Peugeot 208" />
                </div>
              </div>

              {/* Intervention address (optional) */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="intervention_differs"
                    checked={data.intervention_address_differs}
                    onCheckedChange={(v) => update('intervention_address_differs', !!v)}
                    data-testid="manual-intervention-differs"
                  />
                  <Label htmlFor="intervention_differs" className="text-sm cursor-pointer">
                    Adresse d&apos;intervention différente de l&apos;adresse de facturation
                  </Label>
                </div>
                {data.intervention_address_differs && (
                  <div>
                    <Label className="text-xs">Adresse d&apos;intervention</Label>
                    <Input
                      value={data.intervention_address}
                      onChange={(e) => update('intervention_address', e.target.value)}
                      placeholder="Ex: Parking Carrefour, 06400 Cannes"
                      className="bg-white"
                      data-testid="manual-intervention-address"
                    />
                  </div>
                )}
              </div>

              {/* Intervention date (optional) */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="date_differs"
                    checked={data.intervention_date_differs}
                    onCheckedChange={(v) => update('intervention_date_differs', !!v)}
                    data-testid="manual-date-differs"
                  />
                  <Label htmlFor="date_differs" className="text-sm cursor-pointer">
                    Date d&apos;intervention différente de la date de facturation
                  </Label>
                </div>
                {data.intervention_date_differs && (
                  <div className="max-w-xs">
                    <Label className="text-xs">Date d&apos;intervention</Label>
                    <Input
                      type="date"
                      value={data.intervention_date}
                      onChange={(e) => update('intervention_date', e.target.value)}
                      className="bg-white"
                      data-testid="manual-intervention-date"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-3">
                <Button onClick={() => setStep(2)} disabled={!step1Valid} className="gap-2 bg-reco-500 hover:bg-reco-600" data-testid="manual-next-btn">
                  Suivant <ArrowRight size={16} />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 mt-2">
              {/* Type-specific */}
              {data.invoice_type === 'crevaison' && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold mb-3">Réparation crevaison</h3>
                  <div className="max-w-xs">
                    <Label className="text-xs">Montant TTC *</Label>
                    <Input type="number" step="0.01" min="0" value={data.repair_price} onChange={(e) => update('repair_price', e.target.value)} data-testid="manual-repair-price" placeholder="Ex: 80" />
                  </div>
                </div>
              )}

              {data.invoice_type === 'montage' && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <h3 className="text-sm font-semibold">Montage pneus</h3>
                  {/* Row 1 — Tire size (Largeur / Hauteur / Diamètre) + Marque */}
                  <div className="grid grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Largeur *</Label>
                      <Input type="number" min="0" step="1" value={data.tire_width} onChange={(e) => update('tire_width', e.target.value)} placeholder="205" data-testid="manual-width" className="h-10" />
                    </div>
                    <div>
                      <Label className="text-xs">Hauteur *</Label>
                      <Input type="number" min="0" step="1" value={data.tire_height} onChange={(e) => update('tire_height', e.target.value)} placeholder="55" data-testid="manual-height" className="h-10" />
                    </div>
                    <div>
                      <Label className="text-xs">Diamètre *</Label>
                      <Select value={data.tire_diameter} onValueChange={(v) => update('tire_diameter', v)}>
                        <SelectTrigger className="bg-white h-10" data-testid="manual-diameter">
                          <SelectValue placeholder="Ex: 16" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {DIAMETERS.map((d) => (
                            <SelectItem key={d} value={d.toString()}>R{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Marque (facultatif)</Label>
                      <Input value={data.tire_brand} onChange={(e) => update('tire_brand', e.target.value)} placeholder="Ex: Michelin" data-testid="manual-brand" className="h-10" />
                    </div>
                  </div>
                  {/* Row 2 — Quantity + prices, all same height, aligned */}
                  <div className="grid grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Quantité *</Label>
                      <Input type="number" min="1" step="1" value={data.quantity} onChange={(e) => update('quantity', e.target.value)} data-testid="manual-qty" className="h-10" />
                    </div>
                    <div>
                      <Label className="text-xs">PU pneu TTC (€)</Label>
                      <Input type="number" step="0.01" min="0" value={data.price_tire} onChange={(e) => update('price_tire', e.target.value)} data-testid="manual-price-tire" className="h-10" />
                    </div>
                    <div>
                      <Label className="text-xs">PU montage TTC (€)</Label>
                      <Input type="number" step="0.01" min="0" value={data.price_montage} onChange={(e) => update('price_montage', e.target.value)} data-testid="manual-price-montage" className="h-10" />
                    </div>
                    <div />
                  </div>
                  {/* Row 3 — Travel */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox id="has_travel" checked={data.has_travel} onCheckedChange={(v) => update('has_travel', !!v)} data-testid="manual-has-travel" />
                      <Label htmlFor="has_travel" className="text-sm cursor-pointer">Ajouter un forfait déplacement</Label>
                    </div>
                    {data.has_travel && (
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label className="text-xs">Déplacement TTC (€) *</Label>
                          <Input type="number" step="0.01" min="0" value={data.travel_price} onChange={(e) => update('travel_price', e.target.value)} data-testid="manual-travel-price" className="h-10" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {data.invoice_type === 'libre' && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2">
                  <h3 className="text-sm font-semibold mb-2">Lignes personnalisées</h3>
                  {data.custom_lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        <Label className="text-xs">Description</Label>
                        <Input value={l.description} onChange={(e) => updateLine(i, 'description', e.target.value)} data-testid={`manual-line-${i}-desc`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qté</Label>
                        <Input type="number" min="1" value={l.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} data-testid={`manual-line-${i}-qty`} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">PU TTC (€)</Label>
                        <Input type="number" step="0.01" min="0" value={l.unit_price} onChange={(e) => updateLine(i, 'unit_price', e.target.value)} data-testid={`manual-line-${i}-price`} />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        {data.custom_lines.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-500 hover:bg-red-50" data-testid={`manual-line-${i}-remove`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addLine} className="gap-1.5 mt-1" data-testid="manual-add-line">
                    <Plus size={14} /> Ajouter une ligne
                  </Button>
                </div>
              )}

              {/* Live totals */}
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <h3 className="text-sm font-semibold mb-3">Aperçu des lignes</h3>
                {computed.lines.length === 0 ? (
                  <div className="text-sm text-gray-400">Renseignez les montants pour voir l&apos;aperçu…</div>
                ) : (
                  <div className="space-y-1 text-sm">
                    {computed.lines.map((l, i) => (
                      <div key={i} className="flex justify-between gap-4 py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700">{l.description}</span>
                        <span className="text-gray-500 whitespace-nowrap">
                          {l.qty} × {l.pu_ht.toFixed(2)} € HT = <strong className="text-gray-900">{l.total_ttc.toFixed(2)} € TTC</strong>
                        </span>
                      </div>
                    ))}
                    <div className="pt-2 mt-2 border-t border-gray-300 space-y-0.5 text-xs">
                      <div className="flex justify-between"><span>Total HT</span><span>{computed.total_ht.toFixed(2)} €</span></div>
                      <div className="flex justify-between"><span>TVA 20%</span><span>{computed.tva_amount.toFixed(2)} €</span></div>
                      <div className="flex justify-between text-base font-bold mt-1"><span>Total TTC</span><span data-testid="manual-total-ttc">{computed.total_ttc.toFixed(2)} €</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mode de paiement *</Label>
                  <Select value={data.payment_method} onValueChange={(v) => update('payment_method', v)}>
                    <SelectTrigger className="bg-white" data-testid="manual-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {PAYMENT_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Date de règlement *</Label>
                  <Input type="date" value={data.payment_date} onChange={(e) => update('payment_date', e.target.value)} className="bg-white" data-testid="manual-payment-date" />
                </div>
              </div>

              {/* Invoice number (editable — exceptional use) */}
              <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-2">
                <div>
                  <Label className="text-xs font-medium">Numéro de facture</Label>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Prochain N° partagé (auto + manuel) : <strong className="text-gray-800">{nextNaturalNumber || '…'}</strong>.
                    Vous pouvez forcer un numéro différent (cas exceptionnel).
                  </p>
                </div>
                <Input
                  value={data.override_number}
                  onChange={(e) => update('override_number', e.target.value.toUpperCase())}
                  placeholder={nextNaturalNumber || 'F-2026-0250'}
                  className="max-w-xs font-mono"
                  data-testid="manual-override-number"
                />
                {data.override_number && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                    <WarningCircle size={14} className="flex-shrink-0 mt-0.5" weight="fill" />
                    <div>
                      Vous forcez le N° <strong>{data.override_number}</strong>. Le compteur global
                      restera sur sa séquence naturelle : la prochaine facture (auto ou manuelle)
                      sera <strong>{nextNaturalNumber}</strong>. Attention aux règles françaises
                      (pas de trou, pas de doublon).
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-3">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                  <ArrowLeft size={16} /> Retour
                </Button>
                <Button onClick={generate} disabled={!step2Valid || submitting} className="gap-2 bg-reco-500 hover:bg-reco-600" data-testid="manual-generate-btn">
                  {submitting ? <Spinner size={16} className="animate-spin" /> : <FileText size={16} />}
                  Générer et prévisualiser
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview screen — reuses the 95vw x 95vh custom overlay */}
      {step === 3 && previewUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
          data-testid="manual-preview-overlay"
        >
          <div className="bg-white rounded-md shadow-2xl flex flex-col overflow-hidden" style={{ width: '95vw', height: '95vh' }}>
            <div className="flex items-center justify-between px-4 bg-gray-900 text-white border-b border-gray-800 flex-shrink-0" style={{ height: '40px' }}>
              <span className="text-sm font-medium truncate">
                {generatedNumber} — Facture manuelle générée
              </span>
              <div className="flex items-center gap-1">
                <button onClick={downloadPdf} className="p-1.5 hover:bg-white/10 rounded" title="Télécharger" data-testid="manual-preview-download">
                  <DownloadSimple size={16} />
                </button>
                <button onClick={() => { const w = window.open(previewUrl, '_blank'); if (w) w.onload = () => w.print(); }} className="p-1.5 hover:bg-white/10 rounded" title="Imprimer" data-testid="manual-preview-print">
                  <Printer size={16} />
                </button>
                <button onClick={closeAll} className="p-1.5 hover:bg-white/10 rounded ml-1" title="Fermer" data-testid="manual-preview-close">
                  <X size={16} />
                </button>
              </div>
            </div>
            <iframe src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`} title="Aperçu facture manuelle" className="flex-1 w-full border-0 bg-gray-100" />
          </div>
        </div>
      )}
    </>
  );
}
